import { useState } from "react";
import { motion } from "framer-motion";
import {
  DownloadSimple,
  MapPin,
  Clock,
  Sun,
  ForkKnife,
  Airplane,
  Buildings,
  Sparkle,
  Star,
  Bed,
  Compass,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import { ShareTrip } from "./ShareTrip";
import { PartnerLogo } from "./PartnerLogo";
import { track } from "../lib/analytics";
import { itineraryPdfUrl } from "../lib/api";
import { toast } from "sonner";

function categoryIcon(cat = "") {
  const c = cat.toLowerCase();
  if (c.includes("food")) return ForkKnife;
  if (c.includes("transfer") || c.includes("flight")) return Airplane;
  if (c.includes("stay") || c.includes("hotel")) return Buildings;
  if (c.includes("activity") || c.includes("adventure")) return Sparkle;
  return Sun;
}

export function TripBoard({ itin, slug, readOnly = false }) {
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    if (downloading) return;
    track("pdf_download_started", {
      itinerary_id: itin.id,
      country: itin.country,
    });
    setDownloading(true);
    toast("Preparing your PDF…", { duration: 2500 });
    try {
      // Fetch as blob so we stay in the same user gesture and bypass popup blockers
      // (works on Safari iOS too). Backend sends Content-Disposition: attachment.
      const res = await fetch(itineraryPdfUrl(itin.id));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flycher-${(itin.country || "trip").toLowerCase().replace(/\s+/g, "-")}-${(itin.id || "").slice(0, 6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a moment to start the download before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      track("pdf_download_success", { itinerary_id: itin.id });
    } catch (err) {
      track("pdf_download_failed", { itinerary_id: itin.id, error: String(err) });
      toast.error("Couldn't download the PDF. Please retry.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div id="trip-board" className="mt-16" data-testid="trip-board">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="kicker">— Your trip-board</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-2 text-slate-900">
              {itin.title}
            </h2>
            <div className="text-slate-500 mt-2 text-sm font-mono">
              {itin.dates} · {itin.country}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <ShareTrip itinerary={itin} />
            <div className="flex gap-2">
              {!readOnly && slug && (
                <a
                  href={`/experts/${slug}`}
                  className="px-5 py-3 rounded-full text-sm border border-slate-200 bg-white hover:border-slate-300 text-slate-700"
                >
                  Refine with expert
                </a>
              )}
              <button
                type="button"
                data-testid="btn-download-pdf"
                onClick={downloadPdf}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold shadow-md shadow-sky-200/70 text-white disabled:opacity-60"
                style={{ background: "#2A7FB8" }}
              >
                <DownloadSimple size={16} weight="bold" />
                {downloading ? "Preparing…" : "Download PDF"}
              </button>
            </div>
          </div>
        </div>

        {itin.summary && (
          <p className="text-slate-700 max-w-3xl leading-relaxed mb-4">
            {itin.summary}
          </p>
        )}
        {itin.estimated_cost_inr && (
          <div className="font-mono text-sm font-semibold text-[#2A7FB8] mb-8">
            Estimated cost · {itin.estimated_cost_inr}
          </div>
        )}

        <div className="space-y-6">
          {itin.days.map((day) => (
            <motion.div
              key={day.day}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="card-surface rounded-2xl p-6"
              data-testid={`day-${day.day}`}
            >
              <div className="flex flex-wrap items-end justify-between gap-2 mb-5">
                <div>
                  <div className="kicker">
                    Day {day.day} · {day.city}
                  </div>
                  <h3 className="font-serif text-3xl mt-1 text-slate-900">
                    {day.title}
                  </h3>
                </div>
                {day.date && (
                  <div className="font-mono text-xs text-slate-500">
                    {day.date}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {day.items.map((it, i) => {
                  const Icon = categoryIcon(it.category);
                  return (
                    <div
                      key={i}
                      className="grid grid-cols-[60px,1fr] gap-4 p-4 rounded-xl border border-slate-200 bg-[#FDF8EE]"
                    >
                      <div className="font-mono text-xs text-[#FF7E4D] font-semibold flex items-start gap-1.5">
                        <Clock size={12} weight="bold" /> {it.time}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon size={14} color="#2A7FB8" weight="duotone" />
                          <span className="font-semibold text-sm text-slate-900">
                            {it.title}
                          </span>
                          {it.category && (
                            <span className="text-[10px] tracking-widest uppercase text-slate-500">
                              · {it.category}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                          {it.description}
                        </div>
                        {it.cost_inr && (
                          <div className="font-mono text-xs text-[#2A7FB8] font-semibold mt-2">
                            {it.cost_inr}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {day.stay && (
                <StayCard stay={day.stay} day={day.day} city={day.city} />
              )}

              {day.transport && (day.transport.summary || day.transport.provider) && (
                <a
                  href={day.transport.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((day.transport.provider || "") + " " + day.city)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`day-${day.day}-transport`}
                  onClick={() =>
                    track("transport_tip_clicked", {
                      day: day.day,
                      city: day.city,
                      mode: day.transport.mode,
                    })
                  }
                  className="group block rounded-2xl border border-[#2A7FB8]/15 bg-[#EAF4FB]/40 hover:bg-[#EAF4FB] transition-colors px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#2A7FB8] font-semibold">
                      <Compass size={13} weight="fill" />
                      Get around · Day {day.day}
                    </span>
                    {day.transport.cost_inr && (
                      <span className="font-mono text-xs text-[#2A7FB8] font-semibold">
                        {day.transport.cost_inr}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    {day.transport.mode && (
                      <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md text-[#2A7FB8] bg-white">
                        {day.transport.mode}
                      </span>
                    )}
                    {day.transport.provider && (
                      <span className="text-sm font-semibold text-slate-900 truncate">
                        {day.transport.provider}
                      </span>
                    )}
                    {day.transport.duration && (
                      <span className="text-[11px] text-slate-500">· {day.transport.duration}</span>
                    )}
                  </div>
                  {day.transport.summary && (
                    <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                      {day.transport.summary}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
                    {day.transport.area && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} weight="duotone" /> {day.transport.area}
                      </span>
                    )}
                    <span className="ml-auto text-[10px] text-slate-400 inline-flex items-center gap-1 group-hover:text-[#FF7E4D] transition-colors">
                      Open in Maps <ArrowSquareOut size={10} />
                    </span>
                  </div>
                </a>
              )}

              {day.food_picks?.length > 0 && (
                <div
                  data-testid={`day-${day.day}-food`}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100">
                    <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                      <ForkKnife size={13} weight="fill" color="#FF7E4D" />
                      Where to eat · Day {day.day}
                    </span>
                    <span className="text-[10px] text-slate-400">{day.city}</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {day.food_picks.map((fp, fi) => (
                      <a
                        key={fi}
                        data-testid={`day-${day.day}-food-${fi}`}
                        href={fp.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fp.name + " " + day.city)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block px-4 py-3 hover:bg-[#FDF8EE]/60 transition-colors"
                        onClick={() =>
                          track("food_pick_clicked", {
                            day: day.day,
                            city: day.city,
                            name: fp.name,
                          })
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-slate-900 truncate">
                                {fp.name}
                              </span>
                              {fp.type && (
                                <span
                                  className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ${
                                    /veg|vegan|jain/i.test(fp.type)
                                      ? "text-emerald-700 bg-emerald-50"
                                      : "text-rose-700 bg-rose-50"
                                  }`}
                                >
                                  {fp.type}
                                </span>
                              )}
                            </div>
                            {fp.note && (
                              <p className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">
                                {fp.note}
                              </p>
                            )}
                            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-500">
                              {fp.cuisine && <span>{fp.cuisine}</span>}
                              {fp.cuisine && fp.area && <span>·</span>}
                              {fp.area && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin size={11} weight="duotone" /> {fp.area}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {fp.price_inr && (
                              <span className="font-mono text-xs text-[#2A7FB8] font-semibold">
                                {fp.price_inr}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 inline-flex items-center gap-1 group-hover:text-[#FF7E4D] transition-colors">
                              Open in Maps <ArrowSquareOut size={10} />
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {itin.tips?.length > 0 && (
          <div className="mt-10 card-surface rounded-2xl p-6">
            <span className="kicker">— Insider tips</span>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {itin.tips.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <MapPin size={14} color="#FF7E4D" weight="duotone" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}

function StayCard({ stay, day, city }) {
  const primary = stay.deeplinks?.[0];
  const secondary = stay.deeplinks?.slice(1, 3) || [];
  return (
    <div
      data-testid={`day-${day}-stay`}
      className="mt-5 rounded-2xl overflow-hidden border border-[#2A7FB8]/15 bg-gradient-to-br from-[#EAF4FB] via-white to-[#FFF6E8] shadow-sm"
    >
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <span
          className="w-12 h-12 rounded-2xl grid place-items-center flex-shrink-0 shadow-sm"
          style={{ background: "#2A7FB8" }}
        >
          <Bed size={22} color="#ffffff" weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#2A7FB8]">
              Tonight in {city}
            </span>
            {stay.rating && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#F4B942]">
                <Star size={11} weight="fill" /> {stay.rating}
              </span>
            )}
          </div>
          <div className="font-serif text-xl sm:text-2xl text-slate-900 leading-tight mt-1">
            {stay.name}
          </div>
          {(stay.area || stay.vibe) && (
            <div className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
              {stay.area && <span className="font-medium">{stay.area}</span>}
              {stay.area && stay.vibe && <span> · </span>}
              {stay.vibe}
            </div>
          )}
          {stay.badges?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {stay.badges.map((b) => (
                <span
                  key={b}
                  className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white border border-[#2A7FB8]/20 text-slate-700"
                >
                  {b}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2 sm:gap-2.5 flex-shrink-0">
          {stay.price_inr && (
            <div className="font-mono text-base font-bold text-[#2A7FB8]">
              {stay.price_inr}
            </div>
          )}
          {primary && (
            <a
              href={primary.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`day-${day}-stay-book`}
              onClick={() =>
                track("stay_book_clicked", {
                  day,
                  city,
                  partner: primary.name,
                  hotel: stay.name,
                  price_inr: stay.price_inr,
                })
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white shadow-md shadow-orange-200/70 hover:opacity-95 whitespace-nowrap"
              style={{ background: "#FF7E4D" }}
            >
              <span className="text-white/95">Book on</span>
              <PartnerLogo name={primary.name} size={16} />
              <ArrowSquareOut size={12} weight="bold" />
            </a>
          )}
          {secondary.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-start sm:justify-end">
              {secondary.map((d) => (
                <a
                  key={d.name}
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("stay_secondary_clicked", {
                      day,
                      city,
                      partner: d.name,
                    })
                  }
                  className="text-[10px] font-semibold tracking-tight px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700 hover:text-[#2A7FB8] hover:border-[#2A7FB8]/40"
                >
                  <PartnerLogo name={d.name} size={14} />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
